import { RetryableDonationFormInvariantError } from "../errors"
import { createDonation, createRecurringDonation } from "../queries"
import {
  CreateDonationPayload,
  CreateRecurringDonationPayload,
  DateStampString,
  Designation,
  DonationDesignationResource,
  PayloadMeta,
  FormSubmissionResult,
  RecurringDonationDesignationResource,
  RepeatableFrequency,
  Schedule,
  State,
  StripeToken,
} from "../types"
import { shouldSavePaymentMethod } from "./paymentMethods"
import { frequencyToSchedule } from "./frequency"

type StateForFormSubmission = Pick<
  State,
  | "frequency"
  | "stripePaymentMethod"
  | "savePaymentMethodChecked"
  | "designations"
>

type FormSubmissionParams = {
  state: StateForFormSubmission
  calculatedDesignations: Designation[]
  stripeToken: StripeToken
}

export abstract class FormSubmission<Payload> {
  protected state: StateForFormSubmission
  protected calculatedDesignations: Designation[]
  protected stripeToken: StripeToken
  protected payload?: Payload

  constructor({
    state,
    calculatedDesignations,
    stripeToken,
  }: FormSubmissionParams) {
    this.state = state
    this.calculatedDesignations = calculatedDesignations
    this.stripeToken = stripeToken
    this.buildPayload()
  }

  static create(
    params: FormSubmissionParams,
  ): OneTimeFormSubmission | RecurringFormSubmission {
    const { state } = params
    if (state.frequency.type === "once") {
      return new OneTimeFormSubmission(params)
    } else {
      return new RecurringFormSubmission(params)
    }
  }

  protected buildPayload(): void {}

  abstract submit(): Promise<FormSubmissionResult>

  protected buildPayloadMetaDebug(): PayloadMeta {
    const meta: PayloadMeta = {
      debug_original_designations: this.state.designations,
    }
    if (this.state.stripePaymentMethod?.type) {
      meta.debug_stripe_method_type = this.state.stripePaymentMethod.type
    }
    return meta
  }
}

class OneTimeFormSubmission extends FormSubmission<CreateDonationPayload> {
  async submit() {
    if (!this.payload) {
      throw new RetryableDonationFormInvariantError()
    } else {
      return await createDonation({ payload: this.payload })
    }
  }

  protected buildPayload() {
    this.payload = {
      data: {
        attributes: {
          stripe_token: this.stripeToken,
          store_payment_method: shouldSavePaymentMethod({
            frequency: this.state.frequency,
            stripePaymentMethod: this.state.stripePaymentMethod,
            savePaymentMethodChecked: this.state.savePaymentMethodChecked,
          }),
        },
      },
      included: this.calculatedDesignations.map(this.designationToResource),
      meta: this.buildPayloadMetaDebug(),
    }
  }

  private designationToResource(
    designation: Designation,
  ): DonationDesignationResource {
    return {
      type: "Designation",
      attributes: { amount: designation.amount },
      relationships: {
        fund: {
          data: {
            type: "Fund",
            id: designation.fundId,
          },
        },
      },
    }
  }
}

class RecurringFormSubmission extends FormSubmission<CreateRecurringDonationPayload> {
  async submit() {
    if (!this.payload) {
      throw new RetryableDonationFormInvariantError()
    } else {
      return await createRecurringDonation({ payload: this.payload })
    }
  }

  protected buildPayload(): void {
    this.payload = {
      data: {
        attributes: {
          stripe_token: this.stripeToken,
          schedule: this.schedule,
          next_occurrence: this.nextOccurrence,
        },
      },
      included: this.calculatedDesignations.map(this.designationToResource),
      meta: this.buildPayloadMetaDebug(),
    }
  }

  private get schedule(): Schedule {
    return frequencyToSchedule(this.state.frequency as RepeatableFrequency)
  }

  private get nextOccurrence(): DateStampString {
    return (this.state.frequency as RepeatableFrequency).starting
  }

  private designationToResource(
    designation: Designation,
  ): RecurringDonationDesignationResource {
    return {
      type: "RecurringDonationDesignation",
      attributes: { amount: designation.amount },
      relationships: {
        fund: {
          data: {
            type: "Fund",
            id: designation.fundId,
          },
        },
      },
    }
  }
}
